import { AllSubKeysMessageResource, MessageResource } from '@/i18n/messages';
import {
    AssignTaskStatus,
    DossierButtonAction,
    DossierDetailProcessingStatus,
    DossierHandoverMethod,
    DossierPageCodeFilter,
    DossierSummaryProcessStatus,
    ProcedureStatus,
} from '@/types/enum';
import { MenuFilterRecord, ProcedurePageFilter } from '@/types/interface';

/**
 * Mảng hình giao trả
 * @requires t - hàm dịch ngôn ngữ
 * @returns Mảng các đối tượng chứa nhãn và giá trị
 */
export const GetArrHandoverMethod = (
    transDef: (data: keyof MessageResource['System']) => string
) => [
        {
            label: transDef('System_HandoverMethod_Offline'),
            value: `${DossierHandoverMethod.Offline}`,
        },
        {
            label: transDef('System_HandoverMethod_Online'),
            value: `${DossierHandoverMethod.Online}`,
        },
        {
            label: transDef('System_HandoverMethod_Post'),
            value: `${DossierHandoverMethod.Post}`,
        },
    ];

/** Badge trạng thái xử lý  */
export const DossierDetailStatusBadge: Record<
    DossierDetailProcessingStatus,
    {
        className: string;
        value: AllSubKeysMessageResource<'DossierManagement'>;
    }
> = {
    [DossierDetailProcessingStatus.Pending]: {
        className: 'bg-blue-500 text-white',
        value: 'DossierStatus_Processing',
    },
    [DossierDetailProcessingStatus.Done]: {
        className: 'bg-green-600 text-white',
        value: 'DossierStatus_Complete',
    },
    [DossierDetailProcessingStatus.Revoke]: {
        className: 'bg-red-500 text-white',
        value: 'DossierStatus_Revoked',
    },
    [DossierDetailProcessingStatus.EndDueToCompletion]: {
        className: 'bg-red-500 text-white',
        value: 'DossierStatus_EndDueToCompletion',
    },
};

/** Badge trạng thái xử lý của giao việc */
export const AssignTaskStatusBadge: Record<
    AssignTaskStatus,
    {
        className: string;
        value: AllSubKeysMessageResource<'DossierManagement'>;
    }
> = {
    [AssignTaskStatus.Pending]: {
        className: 'bg-blue-500 text-white',
        value: 'DossierStatus_Processing',
    },
    [AssignTaskStatus.Done]: {
        className: 'bg-green-600 text-white',
        value: 'DossierStatus_Complete',
    },
    [AssignTaskStatus.Revoke]: {
        className: 'bg-green-600 text-white',
        value: 'DossierStatus_Complete',
    },
};

/** Badge trạng thái xử lý của summary process */
export const SummaryProcessStatusBadge: Record<
    DossierSummaryProcessStatus,
    {
        className: string;
        value: AllSubKeysMessageResource<'DossierManagement'>;
    }
> = {
    [DossierSummaryProcessStatus.Waiting]: {
        className: 'bg-blue-500 text-white',
        value: 'DossierStatus_WaittingReceive',
    },
    [DossierSummaryProcessStatus.Processing]: {
        className: 'bg-blue-500 text-white',
        value: 'DossierStatus_Processing',
    },
    [DossierSummaryProcessStatus.Fininshed]: {
        className: 'bg-green-600 text-white',
        value: 'DossierStatus_Complete',
    },
    [DossierSummaryProcessStatus.Cancel]: {
        className: 'bg-red-500 text-white',
        value: 'DossierStatus_Cancel',
    },
    [DossierSummaryProcessStatus.Refuse]: {
        className: 'bg-amber-500 text-white',
        value: 'DossierStatus_Refuse',
    },
};

/**
 * Nhãn ACtionTriggerTrace của thông báo hồ sơ
 */
const DossierNotificationDict = {
    /**Tạo dừng tính */
    RequestPause_Create: 'CreateRequestPause',
    /**Duyệt yêu cầu dừng tính */
    RequestPause_Approve: 'ApproveRequestPause',
    /**Từ chối yêu cầu dừng tính */
    RequestPause_Refuse: 'RefuseRequestPause',
    /**Chuyển duyệt yêu cầu dừng tính */
    RequestPause_Forward: 'ForwardRequestPause',
    /**Hoàn thành tiếp nhận bổ sung */
    RequestPause_AdditionalReceive: 'AdditionalReceiveRequestPause',
    /**Công dân nộp bổ sung */
    RequestPause_CustomerAdditional: 'CustomerAdditional',
    /**Tiếp tục xử lý, cancel */
    RequestPause_Cancle: 'CanclePause',
};

/**Nhãn ngôn ngữ trong cây quy trình xử lý và trạng thái thông báo hồ sơ */
export const ProcessActionDirectories: Record<
    string,
    AllSubKeysMessageResource<'DossierManagement'>
> = {
    [DossierButtonAction.EMP_Creates]: 'DossierAction_Receive',
    [DossierButtonAction.EMP_ReceiveManyOnline]: 'DossierAction_Receive',
    [DossierButtonAction.EMP_ReceiveOnline]: 'DossierAction_Receive',
    [DossierButtonAction.EMP_ReceiveCompleted]: 'DossierAction_ReceiveComplete',
    [DossierButtonAction.EMP_Forward]: 'DossierAction_ForwardNext',
    [DossierButtonAction.EMP_PrevForward]: 'DossierAction_ForwardPrev',
    [DossierButtonAction.EMP_Revoke]: 'DossierAction_Revoke',
    [DossierButtonAction.EMP_ApproveDossier]: 'DossierAction_ApproveDossier',
    [DossierButtonAction.EMP_RefuseDossier]: 'DossierAction_RefuseDossier',
    [DossierButtonAction.EMP_PrintReceiptsAfterCreated]: 'DossierAction_PrintReceiptsAfterCreated',
    [DossierButtonAction.EMP_ReturnResult]: 'DossierAction_ReturnResult',
    [DossierButtonAction.EMP_StopDossierToCouting]: 'DossierAction_StopDossierToCouting',
    [DossierButtonAction.EMP_Cancel]: 'DossierAction_Cancel',
    [DossierButtonAction.EMP_AssignTask]: 'DossierAction_AssignTask',
    [DossierButtonAction.EMP_ReturnAssignTask]: 'DossierAction_ReturnAssignTask',
    [DossierButtonAction.EMP_ReceiveConnect]: 'DossierAction_ReceiveConnect',
    [DossierButtonAction.EMP_Coordination]: 'DossierAction_Coordination',
    [DossierButtonAction.EMP_ReturnCoordination]: 'DossierAction_ReturnCoordination',
    [DossierButtonAction.EMP_RefuseAskAdvise]: 'DossierAction_RefuseAskAdvise',
    [DossierButtonAction.EMP_RefuseConnect]: 'DossierAction_RefuseConnect',
    [DossierButtonAction.EMP_DecisionStopCounting]: 'DossierAction_DecisionStopCounting',
    [DossierButtonAction.EMP_RefuseStopCounting]: 'DossierAction_RefuseStopCounting',
    [DossierButtonAction.EMP_ReceiveAdditional]: 'DossierAction_ReceiveAdditional',
    [DossierButtonAction.EMP_ForwardRequestStopCounting]: 'DossierAction_ForwardRequestStopCounting',
    [DossierButtonAction.EMP_CancelStopCounting]: 'DossierAction_CancelStopCounting',
    [DossierButtonAction.EMP_SendConnection]: 'DossierAction_SendConnect',
    [DossierButtonAction.EMP_AskAdvise]: 'DossierAction_AskAdvise',
    [DossierButtonAction.EMP_ReceiveAskAdvise]: 'DossierAction_ReceiveAskAdvise',
    [DossierButtonAction.EMP_ReturnConnection]: 'DossierAction_ReturnConnect',
    [DossierButtonAction.EMP_ReturnAskAdvise]: 'DossierAction_ReturnAskAdvise',
    [DossierButtonAction.EMP_ContinueHandle]: 'DossierAction_CountinueHandle',
    [DossierButtonAction.EMP_ThirdPartySystem]: 'DossierAction_ForwardToThirdParty',
    // [DossierButtonAction.EMP_ReAssignProcess]: 'DossierAction_ReAssignProcess',

    // Mockup thông báo hồ sơ -------------------------------------
    Dossier: 'DossierStatus_Notification',
    [DossierNotificationDict.RequestPause_Create]: 'DossierStatus_RefuseRequestStopCounting',
    [DossierNotificationDict.RequestPause_Approve]: 'DossierNotification_ApproveStopCounting',
    [DossierNotificationDict.RequestPause_Refuse]: 'DossierNotification_RefuseStopCounting',
    [DossierNotificationDict.RequestPause_Forward]: 'DossierNotification_ForwardRequestStopCounting',
    [DossierNotificationDict.RequestPause_AdditionalReceive]: 'DossierNotification_FinishRequestStopCounting',
    [DossierNotificationDict.RequestPause_CustomerAdditional]: 'DossierNotification_CustAdditional',
    [DossierNotificationDict.RequestPause_Cancle]: 'DossierNotification_ContinueHandle',
};

/**Lưu thông tin filter danh sách hồ sơ theo từng menu riêng biệt */
export const DossierFilterDirectories: Record<string, MenuFilterRecord> = {
    /**Công việc Quan trong */
    'job-important': {
        pageCode: DossierPageCodeFilter.CV_QuanTrong,
        title: 'Sidebar_SubmittedAndWait',
        keyMenuFilter: DossierPageCodeFilter.CV_QuanTrong,
        className: 'text-blue-600',
        isJob: true,
    },

    /**Đã nộp - Chờ tiếp nhận */
    'job-waiting-receive': {
        pageCode: DossierPageCodeFilter.CV_DaNopChoTiepNhan,
        title: 'Sidebar_SubmittedAndWait',
        keyMenuFilter: DossierPageCodeFilter.CV_DaNopChoTiepNhan,
        className: 'text-green-600',
        isJob: true,
    },
    /**Công việc đang xử lý */
    'job-processing': {
        pageCode: DossierPageCodeFilter.CV_DangXuLy,
        title: 'Sidebar_Processing',
        keyMenuFilter: DossierPageCodeFilter.CV_DangXuLy,
        className: 'text-red-500',
        isJob: true,
    },
    /**Công việc đã chuyển */
    'job-transferred': {
        pageCode: DossierPageCodeFilter.CV_DaChuyen,
        title: 'Sidebar_Transferred',
        keyMenuFilter: DossierPageCodeFilter.CV_DaChuyen,
        className: 'text-red-700',
        isJob: true,
    },

    /**Hồ sơ quá hạn */
    overdue: {
        pageCode: DossierPageCodeFilter.HoSoQuaHan,
        title: 'Sidebar_Overdue',
        keyMenuFilter: DossierPageCodeFilter.HoSoQuaHan,
        className: 'text-red-600',
    },

    /**Hồ sơ sắp đến hạn */
    'due-date': {
        pageCode: DossierPageCodeFilter.HoSoSapDenHan,
        title: 'Sidebar_ExpiringSoon',
        keyMenuFilter: DossierPageCodeFilter.HoSoSapDenHan,
        className: 'text-rose-500',
    },
    /**Đã tiếp nhận - chờ chuyển */
    'received-waitting-transer': {
        pageCode: DossierPageCodeFilter.DaTiepNhanChoChuyen,
        title: 'Sidebar_ReceivedAndWaitTransfer',
        keyMenuFilter: DossierPageCodeFilter.DaTiepNhanChoChuyen,
        className: 'text-gray-500',
    },
    /**Đang xử lý */
    processing: {
        pageCode: DossierPageCodeFilter.DangXuLy,
        title: 'Sidebar_Processing',
        keyMenuFilter: DossierPageCodeFilter.DangXuLy,
        className: 'text-blue-500',
    },
    /**Dừng tính */
    stop: {
        pageCode: DossierPageCodeFilter.DungTinh,
        title: 'Sidebar_StopCounting',
        keyMenuFilter: 'DungTinh',
        className: 'text-red-500',
    },
    /**Chờ giao trả */
    'waiting-return': {
        pageCode: DossierPageCodeFilter.ChoGiaoTra,
        title: 'Sidebar_WaitingReturn',
        keyMenuFilter: DossierPageCodeFilter.ChoGiaoTra,
        className: 'text-gray-500',
    },
    /**Đã giao trả */
    returned: {
        pageCode: DossierPageCodeFilter.DaGiaoTra,
        title: 'Sidebar_Returned',
        keyMenuFilter: DossierPageCodeFilter.DaGiaoTra,
        className: 'text-green-600',
    },
    /**Từ chối giải quyết */
    refused: {
        pageCode: DossierPageCodeFilter.TuChoiGiaiQuyet,
        title: 'Sidebar_RefuseToResolve',
        keyMenuFilter: DossierPageCodeFilter.TuChoiGiaiQuyet,
        className: 'text-red-500',
    },
    /**Hủy hồ sơ */
    cancel: {
        pageCode: DossierPageCodeFilter.HuyHoSo,
        title: 'Sidebar_CancelDossier',
        keyMenuFilter: DossierPageCodeFilter.HuyHoSo,
        className: 'text-red-500',
    },
    /**Đang chờ kết quả phối hợp*/
    'waiting-coordination': {
        pageCode: DossierPageCodeFilter.DangChoKetQuaPhoiHop,
        title: 'Sidebar_WaitingCoordination',
        keyMenuFilter: DossierPageCodeFilter.DangChoKetQuaPhoiHop,
        className: 'text-gray-500',
    },
    /**Tất cả trạng thái nhận */
    all: {
        pageCode: DossierPageCodeFilter.TatCaTrangThaiNhan,
        title: 'Sidebar_AllReceivingStatus',
        keyMenuFilter: DossierPageCodeFilter.TatCaTrangThaiNhan,
        className: 'text-gray-600',
    },

    /**Liên thông */
    coordination: {
        pageCode: DossierPageCodeFilter.LienThong,
        title: 'Sidebar_WaitingCoordination',
        keyMenuFilter: DossierPageCodeFilter.LienThong,
        className: 'text-gray-600',
    },
};

export const DirectoriesProcedurePath: Record<string, ProcedurePageFilter> = {
    ['draft']: {
        title: '',
        status: ProcedureStatus.Draft,
        pageCode: '',
    },

    ['in-use']: {
        title: '',
        status: ProcedureStatus.InUse,
        pageCode: '',
    },

    ['waiting']: {
        title: '',
        status: ProcedureStatus.WaitForApprove,
        pageCode: '',
    },

    ['take-effect']: {
        title: '',
        status: ProcedureStatus.TakeEffect,
        pageCode: '',
    },

    ['expire']: {
        title: '',
        status: ProcedureStatus.Expire,
        pageCode: '',
    },
};
